import type { ColorToken, TaskDTO } from '@hearth/shared';
import { clsx } from 'clsx';
import { CalendarDays, MapPin, Plus, Repeat, UserRound, Users } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { OwnerAvatar } from '../../components/crm';
import { Badge, Button, Checkbox, EmptyState } from '../../components/ui';
import { colorClasses } from '../../lib/colors';
import { DEFAULT_LOCALE } from '../../lib/format';
import { TASK_TYPE_ICONS } from '../tasks/taskAppearance';
import {
  type CalendarEntry,
  type VisibleRange,
  addDays,
  entryLabel,
  formatDaySpan,
  formatTime,
  formatTimeRange,
  isCompletable,
  isMultiDay,
  isOverdue,
  isSameDay,
  startOfDay,
} from './model';

const DAY_HEADING = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
});
const SHORT_DAY = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

export type AgendaViewProps = {
  range: VisibleRange;
  entries: CalendarEntry[];
  now: Date;
  canEdit: boolean;
  colorOf: (task: TaskDTO) => ColorToken;
  selectedId: string | null;
  onOpenEntry: (task: TaskDTO, anchor: HTMLElement) => void;
  onToggleDone: (task: TaskDTO, done: boolean) => void;
  /** Lengthens the list; null once it covers as much as one request may. */
  onShowMore: (() => void) | null;
  onNew: () => void;
};

/** When a row happens, as read on its day. */
function rowTime(entry: CalendarEntry, day: Date): string {
  const { task, timing } = entry;
  if (task.allDay) {
    const last = new Date(timing.end.getTime() - 1);
    return isSameDay(last, day) || last < day ? 'All day' : `All day · until ${SHORT_DAY.format(last)}`;
  }
  if (timing.point) return formatTime(timing.start);
  if (!isMultiDay(timing)) return formatTimeRange(timing.start, timing.end);
  if (timing.start < day) return `Until ${SHORT_DAY.format(timing.end)}, ${formatTime(timing.end)}`;
  return `${formatTime(timing.start)} – ${SHORT_DAY.format(timing.end)}, ${formatTime(timing.end)}`;
}

/** Day-by-day list of what is coming up; each entry is listed once, on its first day in the range. */
export function AgendaView({
  range,
  entries,
  now,
  canEdit,
  colorOf,
  selectedId,
  onOpenEntry,
  onToggleDone,
  onShowMore,
  onNew,
}: AgendaViewProps) {
  const groups = useMemo(() => {
    const byDay = new Map<number, CalendarEntry[]>();
    for (const entry of entries) {
      const day = entry.timing.start < range.start ? range.start : startOfDay(entry.timing.start);
      if (day >= range.end) continue;
      const list = byDay.get(day.getTime()) ?? [];
      list.push(entry);
      byDay.set(day.getTime(), list);
    }
    return [...byDay.entries()]
      .sort(([a], [b]) => a - b)
      .map(([time, list]) => {
        const day = new Date(time);
        // All-day and running-over entries head the day; the rest keep their time order.
        const heads = (entry: CalendarEntry) => Number(entry.timing.allDayLane || entry.timing.start < day);
        return { day, entries: [...list].sort((a, b) => heads(b) - heads(a)) };
      });
  }, [entries, range]);

  const last = addDays(range.end, -1);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {groups.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="Nothing scheduled"
          description={`No entries from ${formatDaySpan(range.start, last)} for the people and types selected.`}
          action={
            canEdit ? (
              <Button icon={Plus} onClick={onNew}>
                New entry
              </Button>
            ) : undefined
          }
        />
      ) : (
        groups.map(({ day, entries: dayEntries }) => (
          <section key={day.getTime()} aria-label={DAY_HEADING.format(day)}>
            <h3 className="sticky top-0 z-10 flex items-center gap-2 border-y border-slate-100 bg-surface/95 px-4 py-1.5 text-xs font-semibold text-slate-700 backdrop-blur">
              {DAY_HEADING.format(day)}
              {isSameDay(day, now) && (
                <Badge color="orange" size="sm">
                  Today
                </Badge>
              )}
            </h3>
            <ul className="divide-y divide-slate-100">
              {dayEntries.map((entry) => {
                const { task } = entry;
                const Icon = TASK_TYPE_ICONS[task.type];
                const overdue = isOverdue(task, now);
                const done = task.status === 'completed';
                return (
                  <li
                    key={task.id}
                    className={clsx(
                      'flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-[13px]',
                      selectedId === task.id ? 'bg-slate-50' : 'hover:bg-slate-50',
                    )}
                  >
                    <span className="w-full shrink-0 text-xs text-slate-600 tabular sm:w-44 sm:text-[13px]">
                      {rowTime(entry, day)}
                    </span>
                    <span className="flex w-4 shrink-0 justify-center">
                      {canEdit && isCompletable(task.type) && (
                        <Checkbox
                          aria-label={`${done ? 'Reopen' : 'Mark done'}: ${task.title}`}
                          checked={done}
                          onChange={() => onToggleDone(task, !done)}
                        />
                      )}
                    </span>
                    <Icon
                      size={15}
                      aria-hidden
                      className={clsx('shrink-0', colorClasses(colorOf(task)).text)}
                    />
                    <button
                      type="button"
                      aria-label={entryLabel(entry, now) + (overdue ? ', overdue' : '')}
                      onClick={(event) => onOpenEntry(task, event.currentTarget)}
                      className={clsx(
                        'min-w-0 truncate rounded text-left font-medium hover:text-ember-700',
                        done ? 'text-slate-400 line-through' : 'text-slate-900',
                        task.type === 'note' && 'italic',
                      )}
                    >
                      {task.title}
                    </button>
                    {overdue && (
                      <Badge color="red" size="sm">
                        Overdue
                      </Badge>
                    )}
                    {task.seriesId && (
                      <Repeat size={13} aria-label="Repeats" className="shrink-0 text-slate-400" />
                    )}
                    {task.attendeeIds.length > 0 && (
                      <Users size={13} aria-label="Has guests" className="shrink-0 text-slate-400" />
                    )}
                    {task.businessId && (
                      <Link
                        to={`/leads/${task.businessId}`}
                        className="max-w-56 truncate text-slate-500 hover:text-ember-700"
                      >
                        {task.businessName}
                      </Link>
                    )}
                    {task.contactName && (
                      <span className="flex min-w-0 items-center gap-1 text-slate-500">
                        <UserRound size={13} aria-hidden className="shrink-0" />
                        <span className="truncate">{task.contactName}</span>
                      </span>
                    )}
                    {task.location && (
                      <span className="flex min-w-0 items-center gap-1 text-slate-500">
                        <MapPin size={13} aria-hidden className="shrink-0" />
                        <span className="max-w-48 truncate">{task.location}</span>
                      </span>
                    )}
                    <span className="ml-auto">
                      <OwnerAvatar userId={task.assignedTo} showName={false} />
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
      {onShowMore && (
        <div className="flex justify-center border-t border-slate-100 p-3">
          <Button size="sm" onClick={onShowMore}>
            Show more
          </Button>
        </div>
      )}
    </div>
  );
}
